import React from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Modal, Spacing, Text } from '@reservamos/elements';
import { useTranslation } from 'react-i18next';
import InstallmentsRadio from 'components/purchase/InstallmentsRadio';
import CSSVariablesProvider from 'components/CSSVariablesProvider/CSSVariablesProvider';

/**
 * One brand's fee table: the plans the backend returns keyed by number of months ("2", "3", ...),
 * not a list. Every read here indexes it by term — `Object.keys` for the offered months,
 * `sameTerms` for the per-month comparison, and `paymentPlans[activeCard][months]` on selection.
 */
const installmentsTableShape = PropTypes.objectOf(
  PropTypes.shape({
    total: PropTypes.number.isRequired,
    monthlyPayment: PropTypes.number.isRequired,
    interest: PropTypes.number,
    interestByMonth: PropTypes.number,
  }),
);

const propTypes = {
  visible: PropTypes.bool.isRequired,
  paymentPlans: PropTypes.shape({
    visaMc: installmentsTableShape,
    amex: installmentsTableShape,
  }),
  selectedCard: PropTypes.string.isRequired,
  onSelectPlan: PropTypes.func.isRequired,
  onSelectCard: PropTypes.func.isRequired,
  handleToggleModal: PropTypes.func.isRequired,
  resetInstallmentsPlan: PropTypes.func.isRequired,
  selectedInstallmentsPlan: PropTypes.shape({
    card: PropTypes.string.isRequired,
    months: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    monthlyPayment: PropTypes.number.isRequired,
  }).isRequired,
  total: PropTypes.number.isRequired,
};

const defaultProps = {
  paymentPlans: {},
};

function InstallmentsPlanSelectorOv({
  selectedCard,
  onSelectCard,
  onSelectPlan,
  visible,
  paymentPlans,
  handleToggleModal,
  resetInstallmentsPlan,
  selectedInstallmentsPlan,
  total,
}) {
  const { t } = useTranslation('general');
  const { env, features } = useSelector((state) => state.whitelabelConfig);

  if (!visible) return null;
  // A table is a map keyed by term, so a present-but-empty one still offers nothing — `{}` is
  // truthy. Bail out before the brand-picker derivation below: with no Visa/Mastercard terms
  // there is no list to fall back to, so asking for a brand could only lead to an empty one.
  if (!paymentPlans.visaMc || Object.keys(paymentPlans.visaMc).length === 0) return null;

  /**
   * Whether picking a card brand would actually change the terms on offer.
   * @param {object} a - One brand's plans, keyed by month.
   * @param {object} b - The other brand's plans, keyed by month.
   * @returns {boolean} True when both render the same rows.
   */
  const sameTerms = (a, b) => {
    const months = Object.keys(a).sort();
    const otherMonths = Object.keys(b).sort();
    if (months.length !== otherMonths.length) return false;
    return months.every(
      (month, i) =>
        otherMonths[i] === month &&
        a[month]?.monthlyPayment === b[month]?.monthlyPayment &&
        a[month]?.interestByMonth === b[month]?.interestByMonth,
    );
  };

  // The brand picker only earns its place when the choice changes something. Cielo serves one
  // table for both brands (Fees::CieloFees calls the same tenant lookup for visa_mc and amex),
  // and MercadoPago does the same for saritur, so there the two radios do nothing. An empty amex
  // table is worse than useless: picking it would show an empty list of terms.
  const amexPlans = paymentPlans.amex || {};
  const showBrandPicker =
    Object.keys(amexPlans).length > 0 && !sameTerms(paymentPlans.visaMc, amexPlans);
  // With the picker hidden, visaMc is the only table on offer — never read a stale amex
  // selection left in the store by another purchase.
  const activeCard = showBrandPicker ? selectedCard : 'visaMc';

  const selectPlanHandler = (selectedMonths) => {
    handleToggleModal();

    /**
     * When installments plan is set to 1 month payment, reset installments
     */
    if (selectedMonths === 1) {
      resetInstallmentsPlan();
      return;
    }

    onSelectPlan(activeCard, Number(selectedMonths), paymentPlans);
  };

  const selectedCardPlans = paymentPlans[activeCard];
  const installmentsPlans = Object.keys(selectedCardPlans).map((option) => ({
    months: Number(option),
    monthlyPayment: selectedCardPlans[option].monthlyPayment,
    interest: selectedCardPlans[option].interest,
    interestByMonth: selectedCardPlans[option].interestByMonth,
  }));

  const singlePayment = {
    months: 1,
    monthlyPayment: total,
    interest: 0,
    interestByMonth: 0,
  };

  return (
    <Modal
      title={t('deferred_payment')}
      responsiveSize="L"
      headerColor="primary"
      visible={visible}
      headerButton={false}
      onCloseModal={handleToggleModal}
    >
      <CSSVariablesProvider
        theme={env.theme}
        funnelStyle={features.FUNNEL_STYLE}
        funnelStyleBrandVariation={env.brand.toUpperCase()}
      >
        <Spacing vertical>
          {showBrandPicker && (
            <Spacing vertical size="S">
              <Text weight="semibold" mobileSize="S">
                {t('payment:select_card_type')}
              </Text>

              <Spacing flexGrow>
                <InstallmentsRadio
                  variant="visa"
                  name="visaMc"
                  onClick={onSelectCard}
                  checked={selectedCard === 'visaMc'}
                />
                <InstallmentsRadio
                  variant="amex"
                  name="amex"
                  onClick={onSelectCard}
                  checked={selectedCard === 'amex'}
                />
              </Spacing>
            </Spacing>
          )}
          <Spacing vertical size="S">
            <InstallmentsRadio
              key={`${activeCard}-1`}
              name={`${activeCard}-plan`}
              variant="installments"
              installmentsPlan={singlePayment}
              value={1}
              onClick={() => selectPlanHandler(1)}
              checked={selectedInstallmentsPlan.months === 1}
            />
          </Spacing>
          <Spacing vertical size="S">
            <Text weight="semibold" mobileSize="S">
              {t('payment:select_intallments_plan')}
            </Text>

            {installmentsPlans.map((plan) => (
              <InstallmentsRadio
                key={`${activeCard}-${plan.months}`}
                name={`${activeCard}-plan`}
                variant="installments"
                installmentsPlan={plan}
                value={plan.months}
                onClick={() => selectPlanHandler(plan.months)}
                checked={selectedInstallmentsPlan.months === plan.months}
              />
            ))}
          </Spacing>
        </Spacing>
      </CSSVariablesProvider>
    </Modal>
  );
}

InstallmentsPlanSelectorOv.propTypes = propTypes;
InstallmentsPlanSelectorOv.defaultProps = defaultProps;

export default InstallmentsPlanSelectorOv;
